import { useEffect, useRef } from 'react';
import { useTheme } from '../../redux/themeprovider';
/**
 * A component that applies the selected theme to the DOM when the theme changes
 * Focuses only on applying CSS variables for custom themes
 */
function ThemeApplier() {
    const { theme } = useTheme();
    // Store the event handler reference so we can remove it on cleanup
    const systemThemeHandlerRef = useRef(null);
    // Helper function to get the system theme preference
    const getSystemTheme = () => {
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
        return 'light';
    };
    useEffect(() => {
        // Handle theme changes
        const root = document.documentElement;
        // Apply theme class
        const validThemes = ['light', 'dark', 'mellowYellow', 'purpleRain', 'blueSky', 'system'];
        // First remove all theme classes
        validThemes.forEach(themeClass => {
            root.classList.remove(themeClass);
        });
        // Handle system theme specially
        if (theme.name === 'system') {
            const systemTheme = getSystemTheme();
            root.classList.add(systemTheme);
            root.setAttribute('data-theme', systemTheme);
            // For system theme, we need to listen for changes
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            // Remove any existing listener
            if (systemThemeHandlerRef.current) {
                mediaQuery.removeEventListener('change', systemThemeHandlerRef.current);
            }
            // Create and store new handler
            const handleSystemThemeChange = (e) => {
                const newSystemTheme = e.matches ? 'dark' : 'light';
                root.classList.remove('light', 'dark');
                root.classList.add(newSystemTheme);
                root.setAttribute('data-theme', newSystemTheme);
            };
            // Store reference to handler for cleanup
            systemThemeHandlerRef.current = handleSystemThemeChange;
            // Add the listener
            mediaQuery.addEventListener('change', handleSystemThemeChange);
        }
        else {
            // Add the current theme class for non-system themes
            root.classList.add(theme.name);
            root.setAttribute('data-theme', theme.name);
            // For custom themes, add a class for special styling
            if (['mellowYellow', 'purpleRain', 'blueSky'].includes(theme.name)) {
                root.classList.add('custom-theme');
            }
            else {
                root.classList.remove('custom-theme');
            }
        }
        // Clean up function to remove event listener when component unmounts or theme changes
        return () => {
            if (systemThemeHandlerRef.current) {
                const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
                mediaQuery.removeEventListener('change', systemThemeHandlerRef.current);
            }
        };
    }, [theme.name]);
    /**
     * Helper function to lighten or darken a color
     * @param color - HEX color to adjust
     * @param amount - Amount to lighten (positive) or darken (negative)
     * @returns Adjusted color as HEX
     */
    const adjustColor = (color, amount) => {
        return '#' + color
            .replace(/^#/, '')
            .replace(/../g, color => ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2));
    };
    // This component doesn't render anything visible
    return null;
}
;
export default ThemeApplier;
